import { ChangeDetectionStrategy, Component, effect } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { OiqFormInputComponent } from '@oiq-ui-sdk/form';
import moment from 'moment';
import { CollectionTableFilterBaseComponent } from './filter.component';
import { CollectionFilterDefinition } from './filter.models';
import {
  COLLECTION_FILTER_DATE_ERROR_MAP,
  CollectionFilterDateFormatValidator,
  CollectionFilterDateRangeOrderValidator,
  CollectionFilterFormatPayloadDateRange,
  CollectionFilterFromQueryDateRange,
} from './filter.utils';

@Component({
  selector: 'collection-filter-daterange',
  standalone: true,
  imports: [ReactiveFormsModule, OiqFormInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let _definition = definition();
    @if (_definition) {
      <div class="max-w-96 space-y-4">
        <div class="date-range-inputs grid w-full grid-cols-2 gap-2" [formGroup]="dateRangeForm">
          <oiq-form-input
            type="date"
            label="Start date"
            placeholder="Start date"
            formControlName="start"
            [canClear]="_definition.daterange.canClear"
            [errorMap]="errorMap"
            (event)="onStartDateChange($event)">
          </oiq-form-input>

          <oiq-form-input
            type="date"
            label="End date"
            placeholder="End date"
            formControlName="end"
            [canClear]="_definition.daterange.canClear"
            [errorMap]="errorMap"
            (event)="onEndDateChange($event)">
          </oiq-form-input>
        </div>
        <div class="flex flex-wrap items-center justify-start gap-2">
          <button class="oiq-button oiq-button--sm" type="button" (click)="setDateRange('today')">
            Today
          </button>
          <button
            class="oiq-button oiq-button--sm"
            type="button"
            (click)="setDateRange('yesterday')">
            Yesterday
          </button>
          <button
            class="oiq-button oiq-button--sm"
            type="button"
            (click)="setDateRange('last_week')">
            Last Week
          </button>
          <button
            class="oiq-button oiq-button--sm"
            type="button"
            (click)="setDateRange('this_week')">
            This Week
          </button>
          <button class="oiq-button oiq-button--sm" type="button" (click)="setDateRange('7days')">
            Last 7 Days
          </button>
          <button
            class="oiq-button oiq-button--sm"
            type="button"
            (click)="setDateRange('this_month')">
            This Month
          </button>
          <button
            class="oiq-button oiq-button--sm"
            type="button"
            (click)="setDateRange('last_month')">
            Last Month
          </button>
          <button
            class="oiq-button oiq-button--sm"
            type="button"
            (click)="setDateRange('this_year')">
            This Year
          </button>
        </div>
      </div>
    }
  `,
})
export class CollectionFilterDateRangeComponent extends CollectionTableFilterBaseComponent {
  dateRangeForm = new FormGroup({
    start: new FormControl(null, CollectionFilterDateFormatValidator),
    end: new FormControl(null, [
      CollectionFilterDateFormatValidator,
      CollectionFilterDateRangeOrderValidator('start'),
    ]),
  });

  errorMap = COLLECTION_FILTER_DATE_ERROR_MAP;

  constructor() {
    super();
    effect(() => {
      const _value = this.value();
      const _definition = this.definition();
      if (!_definition) {
        return;
      }
      const _normalized = CollectionFilterFromQueryDateRange(_value, _definition, false);
      if (
        (this.dateRangeForm.value.start || null) == (_normalized?.start || null) &&
        (this.dateRangeForm.value.end || null) == (_normalized?.end || null)
      ) {
        return;
      }
      // Announced, not silent. Now that the controls carry validators, a reset
      // clears their errors in the model, and the SDK field only re-reads what
      // it renders from its own control's event stream — so a silent reset
      // leaves the last message sitting under an emptied field. It costs
      // nothing here: nothing in this component listens to the form's value,
      // and applying is driven by the fields' own (event) output.
      this.dateRangeForm.reset({
        start: _normalized?.start,
        end: _normalized?.end,
      });
    });
  }

  getQuery() {
    return CollectionFilterFormatPayloadDateRange(
      this.dateRangeForm.getRawValue(),
      this.definition()
    );
  }

  onStartDateChange(event: { type: string; data?: any }): void {
    switch (event.type) {
      case 'change':
        this.applyDateRange();
        break;
    }
  }

  onEndDateChange(event: { type: string; data?: any }): void {
    switch (event.type) {
      case 'change':
        this.applyDateRange();
        break;
    }
  }

  /**
   * An invalid range is not a filter, so it never gets applied: an end before
   * the start silently swapped the table for an empty one, and a year segment
   * grown past four digits sent the backend a bound nobody typed. The applied
   * filter is left as it was and the field carries the error until the range
   * makes sense again — closing the dropdown discards the attempt, the way it
   * discards any unapplied edit.
   *
   * "Left as it was" is worth reading literally. The field pushes a value on
   * every keyup, so retyping a year walks the control through whole dates
   * (2026 -> 0002 -> 0202 -> 2027), and each one that is in order gets applied
   * on its way past. When the year the user was actually aiming for turns out to
   * be inverted, what stays applied is the last intermediate that passed, not
   * the range in the fields — which is why the message has to be visible: it is
   * the only thing telling the user the table is not answering what they see.
   */
  applyDateRange(): void {
    const _end = this.dateRangeForm.controls.end;

    // The order check lives on `end`, and Angular re-runs the validators of the
    // control that changed only, so an edit to `start` has to ask for `end`.
    // Deliberately without `emitEvent: false`: the SDK field renders errors
    // from a stream fed by its own control's StatusChange/Touched/Pristine
    // events, so a silenced re-validation sets `endBeforeStart` in the model
    // and shows the user nothing.
    _end.updateValueAndValidity();

    // Saying it is still not showing it: the message stays behind
    // `visibility: hidden` until the field is both invalid and touched or dirty.
    // Editing `start` never touches `end`, so inverting a range from the start
    // field would fail mute — which is the complaint this ticket is about.
    if (_end.invalid) {
      _end.markAsTouched();
    }

    if (this.dateRangeForm.invalid) {
      return;
    }

    this.handleValueChanged();
  }

  setDateRange(
    range:
      | 'today'
      | 'yesterday'
      | 'last_week'
      | 'this_week'
      | '7days'
      | 'this_month'
      | 'last_month'
      | 'this_year'
  ) {
    switch (range) {
      case 'today':
        this.dateRangeForm.reset({
          start: moment().startOf('day').format('YYYY-MM-DD'),
          end: moment().endOf('day').format('YYYY-MM-DD'),
        });
        break;
      case 'yesterday':
        this.dateRangeForm.reset({
          start: moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD'),
          end: moment().subtract(1, 'days').endOf('day').format('YYYY-MM-DD'),
        });
        break;
      case 'last_week':
        this.dateRangeForm.reset({
          start: moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD'),
          end: moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD'),
        });
        break;
      case 'this_week':
        this.dateRangeForm.reset({
          start: moment().startOf('week').format('YYYY-MM-DD'),
          end: moment().endOf('week').format('YYYY-MM-DD'),
        });
        break;
      case '7days':
        this.dateRangeForm.reset({
          start: moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD'),
          end: moment().endOf('day').format('YYYY-MM-DD'),
        });
        break;
      case 'this_month':
        this.dateRangeForm.reset({
          start: moment().startOf('month').format('YYYY-MM-DD'),
          end: moment().endOf('month').format('YYYY-MM-DD'),
        });
        break;
      case 'last_month':
        this.dateRangeForm.reset({
          start: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          end: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        });
        break;
      case 'this_year':
        this.dateRangeForm.reset({
          start: moment().startOf('year').format('YYYY-MM-DD'),
          end: moment().endOf('year').format('YYYY-MM-DD'),
        });
        break;
      default:
        break;
    }
    this.applyDateRange();
  }

  static fromQuery(query: any, definition: CollectionFilterDefinition): { [key: string]: any } {
    return CollectionFilterFromQueryDateRange(query, definition, true);
  }
}
